// =============================================================================
// useRoleManagementHandlers
//
// Responsibility: owns all user-action logic — opening/closing modals,
// dispatching thunks, and showing result feedback.
//
// Pattern mirrors useCreateScheduleHandlers in the DLO module:
//   - Receives dispatch + modal state via props (no internal Redux reads)
//   - Each handler is a plain function — easy to test in isolation
//   - Returns only what the page component needs to wire up to UI
//
// Modal flow:
//   Add:    openAddModal → user fills form → handleSaveRole(values) → closes form → shows info
//   Edit:   openEditModal(role) → user edits → handleSaveRole(values) → closes form → shows info
//   Toggle: handleToggleStatus(role) → dispatches PATCH → shows info only on error
// =============================================================================

import { useToast } from '../../../../../components/LhToastContainer';
import {
  Role,
  RoleFormValues,
  RoleModalState,
  INITIAL_MODAL_STATE,
  UseRoleManagementHandlersProps,
} from '../types';
import {
  createRole,
  updateRole,
  toggleRoleStatus,
  fetchRoles,
  FetchRolesParams,
} from '../../../../../store/thunks/adminThunks';

export const useRoleManagementHandlers = ({
  dispatch,
  modalState,
  setModalState,
}: UseRoleManagementHandlersProps) => {
  const toast = useToast();

  // ─── Modal helpers ─────────────────────────────────────────────────────────

  /** Opens the Add form with a blank slate. */
  const openAddModal = () => {
    setModalState((prev) => ({
      ...prev,
      formVisible: true,
      formMode: 'add',
      editingRole: null,
    }));
  };
  /**
   * Opens the Edit form pre-filled with the given role's current values.
   * Called when the user taps the edit icon on a table row.
   */
  const openEditModal = (role: Role) => {
    setModalState((prev) => ({
      ...prev,
      formVisible: true,
      formMode: 'edit',
      editingRole: role,
    }));
  };

  /** Opens the delete confirmation modal for the given role. */
  const openDeleteModal = (role: Role) => {
    setModalState((prev) => ({
      ...prev,
      deleteVisible: true,
      deletingRole: role,
    }));
  };

  /** Closes the delete confirmation modal and clears the target role. */
  const closeDeleteModal = () => {
    setModalState((prev) => ({
      ...prev,
      deleteVisible: false,
      deletingRole: null,
    }));
  };

  /** Closes the Add/Edit form and clears the editing target. */
  const closeFormModal = () => {
    setModalState((prev) => ({
      ...prev,
      formVisible: false,
      editingRole: null,
    }));
  };

  /** Shows a toast notification using the app-wide LhToastProvider. */
  const showToast = (
    title: string,
    message: string,
    type: 'success' | 'error'
  ) => {
    toast.showToast({
      header: title,
      subtext: message,
      variant: type,
    });
  };

  // ─── CRUD handlers ─────────────────────────────────────────────────────────

  /**
   * Called by RoleFormModal's Save button after local validation passes.
   * Dispatches createRole (Add mode) or updateRole (Edit mode).
   * On success: closes the form, shows success toast.
   * On failure: keeps the form open, shows error toast so the user can retry.
   */
  const handleSaveRole = async (formValues: RoleFormValues) => {
    try {
      if (modalState.formMode === 'add') {
        const result = await dispatch(createRole(formValues));

        if (createRole.fulfilled.match(result)) {
          closeFormModal();
          dispatch(fetchRoles({ page: 0, size: 9999, sortBy: 'roleName', sortDirection: 'asc' }));
          showToast('Success', 'Role created successfully', 'success');
        } else {
          // Close modal first so the error toast is visible above it
          closeFormModal();
          showToast('Error', (result.payload as string) ?? 'Failed to create role.', 'error');
        }
      } else if (modalState.formMode === 'edit' && modalState.editingRole) {
        const result = await dispatch(
          updateRole({ roleId: modalState.editingRole.id, updates: formValues })
        );

        if (updateRole.fulfilled.match(result)) {
          closeFormModal();
          dispatch(fetchRoles({ page: 0, size: 9999, sortBy: 'roleName', sortDirection: 'asc' }));
          showToast('Success', 'Role updated successfully', 'success');
        } else {
          // Close modal first so the error toast is visible above it
          closeFormModal();
          showToast('Error', (result.payload as string) ?? 'Failed to update role.', 'error');
        }
      }
    } catch (err) {
      closeFormModal();
      showToast('Error', 'An unexpected error occurred. Please try again.', 'error');
    }
  };

  /**
   * Called when the user flips the status toggle on a table row.
   * Computes the next status (Enabled ↔ Disabled) and sends a PATCH.
   * Success is silent (the table row updates in-place via Redux).
   * Failure surfaces an error info modal.
   */
  const handleToggleStatus = async (role: Role) => {
    const newStatus = role.status !== 'Enabled';
    const result = await dispatch(toggleRoleStatus({ role, status: newStatus }));

    if (!toggleRoleStatus.fulfilled.match(result)) {
      showToast('Error', (result.payload as string) ?? 'Failed to update role status.', 'error');
    }
    // No success modal for toggling — the table row reflects the change immediately
  };

  // ─── Return ────────────────────────────────────────────────────────────────

  return {
    // Modal openers
    openAddModal,
    openEditModal,
    openDeleteModal,
    closeFormModal,
    closeDeleteModal,
    // CRUD
    handleSaveRole,
    handleToggleStatus,
  };
};
